import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { ChannelSelector } from '../../components/ChannelSelector'
import { SMSSettingsForm } from '../../components/SMSSettingsForm'
import { LINESettingsForm } from '../../components/LINESettingsForm'
import { FlexMessageSelector } from '../../components/FlexMessageSelector'
import { IntervalConditionSelector } from '../../components/IntervalConditionSelector'
import { IntervalTimePicker } from '../../components/IntervalTimePicker'
import useQueryGetMessageConditions from '../../api/hooks/useQueryGetMessageConditions'
import useMutationCreateIntervalMessage from '../../api/hooks/useMutationCreateIntervalMessage'
import { MessagingQueryKey } from '../../keys'
import {
	MessageChannel,
	MessageGroupCondition,
	MessageConditionType,
	IntervalCondition,
} from '../../types'
import { getConditionLabel } from '../../utils/conditionLabels'
import { calculateCredit, getLanguageLabel } from '../../utils/smsCredits'
import { validateTimeFormat } from '../../utils/intervalHelpers'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

import type { CreditData } from '../../utils/smsCredits'

const intervalMessageFormSchema = z
	.object({
		message: z.string().optional(),
		intervalCondition: z.nativeEnum(IntervalCondition),
		intervalValue: z.number().min(0, 'กรุณาระบุค่าที่ถูกต้อง'),
		sendTime: z.string().refine((val) => validateTimeFormat(val) === true, {
			message: 'รูปแบบเวลาไม่ถูกต้อง (ใช้ HH:MM เช่น 09:00)',
		}),
		messageGroupCondition: z.nativeEnum(MessageGroupCondition),
		minimum: z.number().optional(),
		maximum: z.number().optional(),
		allowResend: z.boolean(),
		channels: z
			.array(z.nativeEnum(MessageChannel))
			.min(1, 'กรุณาเลือกอย่างน้อย 1 ช่องทาง'),
		providerId: z.string().optional(),
		senderId: z.string().optional(),
		hotGameFlexMessageId: z.string().optional(),
	})
	.refine(
		(data) => {
			// Message is required when not using LINE_HOT_GAME
			if (!data.channels.includes(MessageChannel.LINE_HOT_GAME)) {
				return !!data.message && data.message.trim().length > 0
			}
			return true
		},
		{
			message: 'กรุณากรอกข้อความ',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			// Validate message length if provided
			if (data.message && data.message.length > 1000) {
				return false
			}
			return true
		},
		{
			message: 'ข้อความยาวเกินไป (สูงสุด 1000 ตัวอักษร)',
			path: ['message'],
		},
	)
	.refine(
		(data) => {
			// If SMS is selected, require provider and sender
			if (data.channels.includes(MessageChannel.SMS)) {
				return !!data.providerId && !!data.senderId
			}
			return true
		},
		{
			message: 'กรุณาเลือกผู้ให้บริการและชื่อผู้ส่ง SMS',
			path: ['providerId'],
		},
	)
	.refine(
		(data) => {
			// If LINE_HOT_GAME is selected, require hotGameFlexMessageId
			if (data.channels.includes(MessageChannel.LINE_HOT_GAME)) {
				return !!data.hotGameFlexMessageId
			}
			return true
		},
		{
			message: 'กรุณาเลือก Flex Message',
			path: ['hotGameFlexMessageId'],
		},
	)
	.refine(
		(data) => {
			// Validate interval value based on condition type
			if (data.intervalCondition === IntervalCondition.DAY_OF_WEEK) {
				return data.intervalValue >= 0 && data.intervalValue <= 6
			}
			return data.intervalValue >= 1 && data.intervalValue <= 31
		},
		{
			message: 'ค่าช่วงเวลาไม่ถูกต้อง',
			path: ['intervalValue'],
		},
	)
	.refine(
		(data) => {
			// Validate min/max if provided
			if (
				data.minimum !== undefined &&
				data.maximum !== undefined &&
				data.minimum > data.maximum
			) {
				return false
			}
			return true
		},
		{
			message: 'ค่าต่ำสุดต้องน้อยกว่าหรือเท่ากับค่าสูงสุด',
			path: ['minimum'],
		},
	)

type IntervalMessageFormValues = z.infer<typeof intervalMessageFormSchema>

const CreateIntervalMessagePage = () => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { data: conditionsData, isLoading: isLoadingConditions } =
		useQueryGetMessageConditions()
	const createIntervalMessageMutation = useMutationCreateIntervalMessage({
		onSuccess: () => {
			toast.success('สร้างข้อความอัตโนมัติตามช่วงเวลาสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [MessagingQueryKey.GetIntervalMessages],
			})
			navigate('/operator/messaging/interval')
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการสร้างข้อความอัตโนมัติตามช่วงเวลา')
		},
	})

	const form = useForm<IntervalMessageFormValues>({
		resolver: zodResolver(intervalMessageFormSchema),
		defaultValues: {
			message: '',
			intervalCondition: IntervalCondition.DAY_OF_WEEK,
			intervalValue: 1,
			sendTime: '09:00',
			messageGroupCondition: MessageGroupCondition.ALL_MEMBERS,
			allowResend: false,
			channels: [],
			providerId: '',
			senderId: '',
			hotGameFlexMessageId: '',
		},
	})

	const selectedChannels = form.watch('channels')
	const selectedGroupCondition = form.watch('messageGroupCondition')

	const currentCondition = conditionsData?.conditions?.find(
		(c) => c.name === (selectedGroupCondition as any),
	)

	const onSubmit = (values: IntervalMessageFormValues) => {
		createIntervalMessageMutation.mutate({
			message: values.message || '',
			intervalCondition: values.intervalCondition as any,
			intervalValue: values.intervalValue,
			sendTime: values.sendTime,
			messageGroupCondition: values.messageGroupCondition,
			minimum: values.minimum,
			maximum: values.maximum,
			allowResend: values.allowResend,
			channels: values.channels,
			senderId: values.senderId || '',
			hotGameFlexMessageId: values.hotGameFlexMessageId || undefined,
		})
	}

	const message = form.watch('message') || ''

	// Calculate SMS credits if SMS channel is selected
	let creditData: CreditData | null = null
	try {
		if (selectedChannels.includes(MessageChannel.SMS) && message) {
			creditData = calculateCredit(message)
		}
	} catch (error) {
		console.error('Credit calculation error:', error)
	}

	return (
		<PageContainer>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold">
						สร้างข้อความอัตโนมัติตามช่วงเวลา
					</h1>
					<p className="mt-2 text-muted-foreground">
						สร้างข้อความที่จะถูกส่งอัตโนมัติตามช่วงเวลาที่กำหนด
					</p>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>ตั้งค่าการส่งข้อความอัตโนมัติตามช่วงเวลา</CardTitle>
					</CardHeader>
					<CardContent>
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
								className="space-y-6"
							>
								{/* Interval Condition & Value */}
								<div className="rounded-lg border p-4">
									<h3 className="mb-4 text-base font-medium">
										ตั้งค่าช่วงเวลา
									</h3>
									<div className="space-y-4">
										<div className="grid gap-4 md:grid-cols-2">
											<div className="space-y-4">
												<FormField
													control={form.control}
													name="intervalCondition"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<IntervalConditionSelector
																	intervalCondition={field.value}
																	intervalValue={form.watch('intervalValue')}
																	onIntervalConditionChange={(value) => {
																		field.onChange(value)
																		// Reset interval value when condition changes
																		form.setValue(
																			'intervalValue',
																			value === IntervalCondition.DAY_OF_WEEK
																				? 1
																				: 1,
																		)
																	}}
																	onIntervalValueChange={(value) =>
																		form.setValue('intervalValue', value)
																	}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div>
												<FormField
													control={form.control}
													name="sendTime"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<IntervalTimePicker
																	value={field.value}
																	onChange={field.onChange}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</div>
									</div>
								</div>

								{/* Player Condition */}
								<div className="rounded-lg border p-4">
									<h3 className="mb-4 text-base font-medium">
										เงื่อนไขกลุ่มสมาชิก
									</h3>
									<FormField
										control={form.control}
										name="messageGroupCondition"
										render={({ field }) => (
											<FormItem>
												<FormLabel>เลือกกลุ่มสมาชิก</FormLabel>
												<FormControl>
													<Select
														onValueChange={field.onChange}
														value={field.value}
														disabled={isLoadingConditions}
													>
														<SelectTrigger>
															<SelectValue placeholder="เลือกเงื่อนไข" />
														</SelectTrigger>
														<SelectContent>
															{conditionsData?.conditions?.map((condition) => (
																<SelectItem
																	key={condition.name}
																	value={condition.name}
																>
																	{getConditionLabel(condition.name)}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormDescription>
													เลือกกลุ่มสมาชิกที่จะรับข้อความ
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* Condition values */}
									{currentCondition &&
										currentCondition.type === MessageConditionType.SINGLE && (
											<FormField
												control={form.control}
												name="minimum"
												render={({ field }) => (
													<FormItem className="mt-4">
														<FormLabel>ค่าที่ต้องการ</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="กรอกค่าที่ต้องการ"
																{...field}
																onChange={(e) =>
																	field.onChange(
																		e.target.value
																			? Number(e.target.value)
																			: undefined,
																	)
																}
																value={field.value ?? ''}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										)}

									{currentCondition &&
										currentCondition.type === MessageConditionType.MIN_MAX && (
											<div className="mt-4 grid grid-cols-2 gap-4">
												<FormField
													control={form.control}
													name="minimum"
													render={({ field }) => (
														<FormItem>
															<FormLabel>ค่าต่ำสุด</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	placeholder="ค่าต่ำสุด"
																	{...field}
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																				? Number(e.target.value)
																				: undefined,
																		)
																	}
																	value={field.value ?? ''}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="maximum"
													render={({ field }) => (
														<FormItem>
															<FormLabel>ค่าสูงสุด</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	placeholder="ค่าสูงสุด"
																	{...field}
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																				? Number(e.target.value)
																				: undefined,
																		)
																	}
																	value={field.value ?? ''}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										)}
								</div>

								{/* Allow Resend */}
								<FormField
									control={form.control}
									name="allowResend"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel className="text-base">
													อนุญาตให้ส่งซ้ำ
												</FormLabel>
												<FormDescription>
													อนุญาตให้ส่งข้อความไปยังสมาชิกคนเดิมในรอบการส่งถัดไป
												</FormDescription>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/>

								{/* Channels */}
								<FormField
									control={form.control}
									name="channels"
									render={({ field }) => (
										<FormItem>
											<FormControl>
												<ChannelSelector
													value={field.value}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								{/* Channel settings */}
								{selectedChannels.includes(MessageChannel.SMS) && (
									<SMSSettingsForm form={form} />
								)}

								{selectedChannels.includes(MessageChannel.LINE) && (
									<LINESettingsForm />
								)}

								{/* Flex Message Selector for LINE_HOT_GAME */}
								{selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="hotGameFlexMessageId"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Flex Message</FormLabel>
												<FormControl>
													<FlexMessageSelector
														value={field.value}
														onChange={field.onChange}
														disabled={createIntervalMessageMutation.isPending}
													/>
												</FormControl>
												<FormDescription>
													เลือก Flex Message ที่ต้องการส่ง
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{/* Message - optional when using LINE_HOT_GAME */}
								{!selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="message"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ข้อความ</FormLabel>
												<FormControl>
													<Textarea
														placeholder="กรอกข้อความที่ต้องการส่ง"
														className="min-h-[120px]"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													{selectedChannels.includes(MessageChannel.SMS) && (
														<div className="space-y-1">
															{creditData ? (
																<>
																	<div className="flex items-center justify-between text-sm">
																		<span>
																			{creditData.totalMessage -
																				creditData.messageLeft}{' '}
																			/ {creditData.totalMessage} ตัวอักษร (
																			{getLanguageLabel(creditData.lang)})
																		</span>
																		<span className="font-medium">
																			{creditData.credit} เครดิตต่อข้อความ
																		</span>
																	</div>
																</>
															) : (
																<span className="text-sm">
																	กรอกข้อความเพื่อดูจำนวนเครดิต SMS
																</span>
															)}
														</div>
													)}
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{selectedChannels.includes(MessageChannel.LINE_HOT_GAME) && (
									<FormField
										control={form.control}
										name="message"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ข้อความเพิ่มเติม (ถ้ามี)</FormLabel>
												<FormControl>
													<Textarea
														placeholder="กรอกข้อความเพิ่มเติมที่ต้องการส่งพร้อมกับ Flex Message (ไม่บังคับ)"
														className="min-h-[120px]"
														{...field}
													/>
												</FormControl>
												<FormDescription>
													ข้อความนี้จะแสดงพร้อมกับ Flex Message (ถ้ากรอก)
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}

								{/* Submit buttons */}
								<div className="flex gap-3">
									<Button
										type="button"
										variant="outline"
										onClick={() => navigate('/operator/messaging/interval')}
									>
										ยกเลิก
									</Button>
									<Button
										type="submit"
										disabled={
											createIntervalMessageMutation.isPending ||
											selectedChannels.length === 0
										}
									>
										{createIntervalMessageMutation.isPending
											? 'กำลังสร้าง...'
											: 'สร้างข้อความอัตโนมัติตามช่วงเวลา'}
									</Button>
								</div>
							</form>
						</Form>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	)
}

const GuardedCreateIntervalMessagePage = withUserPermission(
	CreateIntervalMessagePage,
	{
		permissionType: PermissionType.Write,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedCreateIntervalMessagePage as CreateIntervalMessagePage }
